"use client";

import { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Plus, RefreshCw, Search } from "lucide-react";
import PermissionGuard from "@/components/PermissionGuard";
import { PermissionSubject } from "@/utils/permissions";
import { useAuthorStore } from "@/stores/authorStore";
import { useDebounce } from "@/hooks/useDebounce";
import ModalCreateAuthor from "@/components/author/ModalCreateAuthor";
import ModalViewAuthor from "@/components/author/ModalViewAuthor";
import ModalEditAuthor from "@/components/author/ModalEditAuthor";
import ModalDeleteAuthor from "@/components/author/ModalDeleteAuthor";
import { API_CONFIG } from "@/config/api";

export default function AuthorPage() {
  const { authors, pagination, isLoading, fetchAuthors } = useAuthorStore();
  const [search, setSearch] = useState("");
  const debounced = useDebounce(search, 500);
  const isSearching = search !== debounced;

  const [openCreate, setOpenCreate] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [openView, setOpenView] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);

  useEffect(() => {
    fetchAuthors({ page: 1, limit: 10, search: debounced });
  }, [debounced, fetchAuthors]);

  const handleRefresh = () => {
    fetchAuthors({ page: pagination.page, limit: pagination.limit, search: debounced });
  };

  const handlePage = (page: number) => {
    fetchAuthors({ page, limit: pagination.limit, search: debounced });
  };

  const handleSearch = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    fetchAuthors({ page: 1, limit: pagination.limit, search });
  };

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="mb-6 flex flex-col justify-between gap-5 lg:flex-row lg:items-center">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600">
                Quản lý tác giả
              </h1>
              <div className="mt-2 flex items-center gap-2">
                <p className="text-gray-500">Danh sách tác giả</p>
                <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200">
                  {pagination.total} tác giả
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            <Button onClick={handleRefresh} variant="outline" className="flex items-center gap-2 hover:cursor-pointer">
              <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
              Làm mới
            </Button>
            <PermissionGuard subject={PermissionSubject.AUTHOR} action="create">
              <Button className="bg-blue-600 text-white hover:bg-blue-700 rounded-lg px-4 py-2 flex items-center gap-2 hover:cursor-pointer" onClick={() => setOpenCreate(true)}>
                <Plus size={20} />
                Thêm tác giả
              </Button>
            </PermissionGuard>
          </div>
        </div>

        <div className="mb-6">
          <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="md:col-span-5 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Tìm kiếm theo tên/thông tin..."
                className="h-10 w-full pl-10 pr-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="md:col-span-1 flex items-stretch justify-end">
              <Button type="submit" disabled={isLoading || isSearching} className="h-10 w-full md:w-auto hover:cursor-pointer">
                {isSearching ? "Đang tìm..." : "Tìm kiếm"}
              </Button>
            </div>
          </form>
        </div>

        <div className="shadow-lg rounded-xl bg-white border border-gray-100 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Đang tải...</span>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50/80 backdrop-blur supports-[backdrop-filter]:bg-gray-50/60">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Tên</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Hình ảnh</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Thông tin</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Thao tác</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {authors.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-6 py-16">
                          <div className="flex flex-col items-center justify-center text-center">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-12 w-12 text-gray-300 mb-3">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.5 20.118a7.5 7.5 0 0 1 15 0A17.933 17.933 0 0 1 12 21.75c-2.68 0-5.216-.586-7.5-1.632Z" />
                            </svg>
                            <p className="text-gray-600 font-medium">Chưa có tác giả</p>
                            <p className="text-gray-400 text-sm mt-1">Hãy thêm tác giả đầu tiên để bắt đầu</p>
                            <PermissionGuard subject={PermissionSubject.AUTHOR} action="create">
                              <Button className="mt-4 hover:cursor-pointer" onClick={() => setOpenCreate(true)}>Thêm tác giả</Button>
                            </PermissionGuard>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      authors.map((a) => (
                        <tr key={a._id} className="transition-all hover:bg-gray-50/60">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{a.name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {a.image ? (
                              <img
                                src={a.image.startsWith('http') ? a.image : `${API_CONFIG.API_FILE_URL}${a.image}`}
                                alt={a.name}
                                className="h-12 w-12 object-cover rounded-md border"
                              />
                            ) : (
                              '-'
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 max-w-[420px] truncate">{a.info || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                            <div className="flex justify-end gap-2">
                              <Button variant="outline" size="sm" className="hover:cursor-pointer" onClick={() => { setSelected(a); setOpenView(true); }}>Xem</Button>
                              <PermissionGuard subject={PermissionSubject.AUTHOR} action="update">
                                <Button variant="outline" size="sm" className="hover:cursor-pointer" onClick={() => { setSelected(a); setOpenEdit(true); }}>Sửa</Button>
                              </PermissionGuard>
                              <PermissionGuard subject={PermissionSubject.AUTHOR} action="delete">
                                <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:cursor-pointer" onClick={() => { setSelected(a); setOpenDelete(true); }}>Xóa</Button>
                              </PermissionGuard>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {pagination.totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between w-full">
                    <div>
                      <p className="text-sm text-gray-700">Trang <span className="font-medium">{pagination.page}</span> / {pagination.totalPages}</p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <Button onClick={() => handlePage(Math.max(1, pagination.page - 1))} disabled={pagination.page === 1} variant="outline" size="sm" className="rounded-l-md hover:cursor-pointer">Trước</Button>
                        {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((p) => (
                          <Button key={p} onClick={() => handlePage(p)} variant={p === pagination.page ? "default" : "outline"} size="sm" className="rounded-none hover:cursor-pointer">{p}</Button>
                        ))}
                        <Button onClick={() => handlePage(Math.min(pagination.totalPages, pagination.page + 1))} disabled={pagination.page === pagination.totalPages} variant="outline" size="sm" className="rounded-r-md hover:cursor-pointer">Sau</Button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <ModalCreateAuthor isOpen={openCreate} onClose={() => setOpenCreate(false)} onComplete={() => fetchAuthors({ page: 1, limit: 10, search: debounced })} />
        <ModalViewAuthor isOpen={openView} onClose={() => setOpenView(false)} authorId={selected?._id || null} />
        <ModalEditAuthor isOpen={openEdit} onClose={() => setOpenEdit(false)} authorId={selected?._id || null} onComplete={() => fetchAuthors({ page: pagination.page, limit: pagination.limit, search: debounced })} />
        <ModalDeleteAuthor isOpen={openDelete} onClose={() => setOpenDelete(false)} author={selected} onComplete={() => fetchAuthors({ page: pagination.page, limit: pagination.limit, search: debounced })} />
      </div>
    </DashboardLayout>
  );
}


